import { useState, useEffect } from 'react';
import { useAuthStore } from '@/lib/stores/authStore';
import { AuthModal } from './AuthModal';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isAuthorized, isLoading, error } = useAuthStore();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('ProtectedRoute state:', { 
      user: user ? { id: user.id, email: user.email, is_authorized: user.is_authorized } : null,
      isAuthenticated, 
      isAuthorized, 
      isLoading, 
      error 
    });
  }, [user, isAuthenticated, isAuthorized, isLoading, error]);

  // Show auth modal if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShowAuthModal(true);
    }
  }, [isLoading, isAuthenticated]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center text-neutral-500">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto mb-2" />
          Loading...
        </div>
      </div>
    );
  }

  // If not authenticated, show auth modal
  if (!isAuthenticated || !user) {
    return (
      <>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            // Redirect to landing page if user closes auth modal
            window.location.href = '/';
          }}
          onSuccess={() => {
            setShowAuthModal(false);
          }}
        />
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center text-neutral-500">
            Please sign in to access this page.
          </div>
        </div>
      </>
    );
  }

  // If authenticated but not authorized, show unauthorized message
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-2">
              Access Denied
            </h2>
            <p className="text-neutral-600 mb-4">
              {error || 'Your account is not authorized to access this application.'}
            </p>
            <button
              onClick={() => {
                window.location.href = '/';
              }}
              className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and authorized, render children
  return <>{children}</>;
}
